import axios, { CancelToken } from "axios";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, Redirect } from "react-router-dom";
import { compose } from "redux";
import {
  BreadcrumbBar,
  IBreadcrumbBarItem,
  IOption,
  ITabProps,
  myAccountLink,
  Tabs,
} from "component-library";
import { AccountType } from "shared-types";
import { i18n } from "i18n";
import { checkIsYp } from "shared-auth";
import withQueryParameters, {
  IWithQueryParameters,
} from "shared/hoc/withQueryParameters";
import { FacetCategory, IFacetGroup } from "shared/models/facet";
import {
  IAgentJobSettings,
  IPerformerJobSettings,
} from "shared/models/settings";

import { navigator } from "shared/services/navigator";
import { MapDispatchToProps } from "shared/types/utilities";
import PageKey from "config/PageKey";

import { IRoute } from "config/routes";
import { IApplicationState } from "store";
import { getFacets } from "store/facet/facet.actions";
import { FacetStoreKey, GetFacets } from "store/facet/facet.types";
import { getJobsPage } from "store/page/page.actions";
import { GetJobsPage } from "store/page/page.types";
import { getPageTranslation } from "store/translation/translation.actions";
import { GetPageTranslation } from "store/translation/translation.types";

import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams, JobItemReadStatus } from "models/api";
import { IClientAlertListResponse } from "models/clientAlert";
import jobService from "services/api/job/jobService";
import selfTapeRequestNotificationService from "services/api/selfTapeRequest/selfTapeRequestNotificationService";
import suggestionsService from "services/api/suggestions/suggestionsService";

import { IClientAlertsRequestParams } from "services/api/suggestions/suggestionsService.types";

import applicationMap from "services/applicationMap";
import Routes from "app-shell/Routes/Routes";
import {
  IWithJobsFilterOptions,
  setupFilterConfig,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  DEFAULT_VALUES,
  FilterType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { FilterModelToJobFilterParamsConverter } from "hoc/withJobsFilter/withJobsFilter.converters";
import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";
import { withJobsFilterOptions as allOpportunitiesWithJobsFilterOptions } from "pages/JobsPage/AllOpportunitiesPage/AllOpportunitiesPage";
import { withJobsFilterOptions as allPaidWithJobsFilterOptions } from "pages/JobsPage/AllPaidPage/AllPaidPage";
import {
  JobsPageTab,
  userTypeToHiddenTabListMap,
} from "pages/JobsPage/JobsPage.config";

import {
  prepareBreadcrumbs,
  prepareCommonTexts,
  prepareTabs,
} from "pages/JobsPage/JobsPage.helpers";
import { withJobsFilterOptions as matchingMyFilterWithJobsFilterOptions } from "pages/JobsPage/MatchingMyFilterPage/MatchingMyFilterPage";
import "./JobsPage.scss";
import getMySuggestionsCount from "pages/JobsPage/shared/getMySuggestionsCount";

export interface IJobsPageActions {
  getPageTranslation: GetPageTranslation;
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  getJobsPage: GetJobsPage;
  getFacets: GetFacets;
}

export interface IJobsPageCommonTexts {
  title: string;
  searchPlaceholder: string;
  sortingOptions: IOption<string>[];
  filterOptions: IOption<string>[];
  placeholders: {
    productionType: string;
    contractType: string;
    dateSent: string;
    sender: string;
    playingAge: string;
    playingAgeFromLabel: string;
    playingAgeFromPlaceholder: string;
    playingAgeToLabel: string;
    playingAgeToPlaceholder: string;
    playingAgeEmptyLabel: string;
    yearLabel: string;
    yearsLabel: string;
    playingAgeOver: string;
    playingAgeUnder: string;
    locationLabel: string;
    searchLocationLabel: string;
    searchLocationPlaceholder: string;
  };
}

export interface IJobsPageStoreProps {
  breadcrumbs: IBreadcrumbBarItem[];
  tabs: { id: JobsPageTab; text: string }[];
  commonTexts: IJobsPageCommonTexts;
  pageTranslationLocale: string;
  facetLocale: string;
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  jobSettings: IPerformerJobSettings | IAgentJobSettings;
  getJobCounts(
    filters: IJobFilterParams[],
    cancellationToken?: CancelToken
  ): Promise<number[]>;
  getClientAlerts(
    params: IClientAlertsRequestParams,
    cancellationToken?: CancelToken
  ): Promise<IClientAlertListResponse>;
  getSelfTapeCount(cancellationToken?: CancelToken): Promise<number>;
}

export interface IJobsPageMatchParams {
  tab: string;
}

interface IJobsPageQueryParams {
  page: number;
}

export type Counters = Record<JobsPageTab, number | undefined>;

export interface IJobsPageInjectedProps {
  userType: AccountType;
  userId: string;
  locale: string;
  commonTexts: IJobsPageCommonTexts;
  pageTranslationLocale: string;
  facetLocale: string;
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  jobSettings: IPerformerJobSettings | IAgentJobSettings;
  refreshCounters(): void;
  restoreScrollPosition: number | null;
  page: number;
}

export interface IJobsPageProps
  extends IJobsPageActions,
    IJobsPageStoreProps,
    IWithPageDataProps,
    IWithQueryParameters<IJobsPageQueryParams>,
    RouteComponentProps<IJobsPageMatchParams> {
  routes: IRoute[];
}

export interface IJobsPageState {
  counters: Counters;
}

export class JobsPage extends React.PureComponent<
  IJobsPageProps,
  IJobsPageState
> {
  private tabToLinkMap = {
    [JobsPageTab.MyFilter]: applicationMap.links.jobsMyFilters(),
    [JobsPageTab.AllPaid]: applicationMap.links.jobsAllPaid(),
    [JobsPageTab.All]: applicationMap.links.jobsAllOpportunities(),
    [JobsPageTab.ClientAlerts]: applicationMap.links.jobsClientAlerts(),
    [JobsPageTab.SelfTapeRequests]: applicationMap.links.jobsSelfTapeRequests(),
    [JobsPageTab.MySuggestions]: applicationMap.links.jobsMySuggestions(),
  };

  public readonly state: Readonly<IJobsPageState> = {
    counters: {
      [JobsPageTab.MyFilter]: undefined,
      [JobsPageTab.AllPaid]: undefined,
      [JobsPageTab.All]: undefined,
      [JobsPageTab.ClientAlerts]: undefined,
      [JobsPageTab.SelfTapeRequests]: undefined,
      [JobsPageTab.MySuggestions]: undefined,
    },
  };

  public componentDidMount() {
    setTimeout(() => void this.loadCounters());
  }

  public render() {
    return (
      <section className="c-jobs-page">
        <div>
          <BreadcrumbBar items={this.props.breadcrumbs} />
          <div className="c-jobs-page__row">
            <Tabs tabs={this.prepareTabs()} />
          </div>
          {this.renderContent()}
        </div>
      </section>
    );
  }

  public renderContent() {
    const {
      user,
      routes,
      commonTexts,
      locale,
      pageTranslationLocale,
      facetLocale,
      productionTypes,
      contractTypes,
      jobSettings,
      restoreScrollPosition,
      queryParameters: { page },
      match,
    } = this.props;
    const excludedRoutes = userTypeToHiddenTabListMap[user.type].map(
      (tab) => this.tabToLinkMap[tab]
    );
    const isYp = checkIsYp();
    const isYpAllowedRoute =
      applicationMap.links.jobsSelfTapeRequests() === match.url;

    if (isYp && !isYpAllowedRoute)
      return <Redirect to={applicationMap.links.jobsSelfTapeRequests()} />;

    return (
      <Routes<IJobsPageInjectedProps>
        items={routes.filter((route) => !excludedRoutes.includes(route.path))}
        injectedProps={{
          commonTexts,
          userType: user.type,
          userId: user.id,
          locale,
          pageTranslationLocale,
          facetLocale,
          productionTypes,
          contractTypes,
          jobSettings,
          refreshCounters: this.loadCounters,
          restoreScrollPosition,
          page,
        }}
      />
    );
  }

  private prepareTabs(): ITabProps[] {
    const { t } = i18n;
    const { tabs, location } = this.props;

    return tabs.map(({ id, text }): ITabProps => {
      const quantity = this.state.counters[id];

      return {
        id,
        click: () => this.handleTabClick(id),
        isActive: this.tabToLinkMap[id] === location.pathname,
        text: t(text, {
          quantity: quantity === undefined ? "" : `(${quantity})`,
        }),
      };
    });
  }

  private handleTabClick = (tabId: JobsPageTab) => {
    navigator.goTo(this.tabToLinkMap[tabId]);
  };

  private loadCounters = async () => {
    const { userType } = this.props;
    const { counters } = this.state;

    if (checkIsYp()) {
      const selfTapesCounts = await this.loadSelfTapeCount();

      return this.setState({
        counters: {
          ...counters,
          [JobsPageTab.SelfTapeRequests]: selfTapesCounts,
        },
      });
    }

    try {
      const [
        [matchingMyFilterCount, allOpportunitiesCount, allPaidCount],
        clientAlertsCount,
        selfTapeCount,
        mySuggestionCount,
      ] = await Promise.all([
        this.loadJobsCount(),
        userType === AccountType.Agent
          ? this.loadClientAlertsCount()
          : undefined,
        this.loadSelfTapeCount(),
        userType === AccountType.Performer && this.loadMySuggestionsCount(),
      ]);

      this.setState({
        counters: {
          ...counters,
          [JobsPageTab.MyFilter]: matchingMyFilterCount,
          [JobsPageTab.All]: allOpportunitiesCount,
          [JobsPageTab.AllPaid]: allPaidCount,
          [JobsPageTab.ClientAlerts]: clientAlertsCount,
          [JobsPageTab.SelfTapeRequests]: selfTapeCount,
          [JobsPageTab.MySuggestions]: mySuggestionCount,
        },
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.error(e);
      }
    }
  };

  private getFilterValues(
    withJobsFilterOptions: IWithJobsFilterOptions
  ): IJobFilterParams {
    const {
      commonTexts,
      productionTypes,
      contractTypes,
      jobSettings,
      userType,
    } = this.props;
    const filterConfig = setupFilterConfig({
      commonTexts,
      productionTypes,
      contractTypes,
      jobSettings,
      userType,
      ...withJobsFilterOptions,
    });
    const defaultFilterValues = {
      readStatus: JobItemReadStatus.Unread,
      pageSize: 0,
    };
    const converter = new FilterModelToJobFilterParamsConverter({
      contractTypes: filterConfig[FilterType.ContractType].facets!,
      productionTypes: filterConfig[FilterType.ProductionType].facets!,
      userType,
      jobSettings: withJobsFilterOptions?.applyUserSettings
        ? jobSettings
        : undefined,
    });

    return {
      ...converter.convert(DEFAULT_VALUES),
      ...defaultFilterValues,
    };
  }

  private async loadJobsCount() {
    const { getJobCounts, userType, cancellationToken } = this.props;

    let counts: number[] = [];

    const allPaidFilterValues = this.getFilterValues(
      allPaidWithJobsFilterOptions
    );
    const matchingMyFilterFilterValues = this.getFilterValues(
      matchingMyFilterWithJobsFilterOptions
    );
    const allOpportunitiesFilterValues = this.getFilterValues(
      allOpportunitiesWithJobsFilterOptions
    );

    const filterSet = [
      matchingMyFilterFilterValues,
      allOpportunitiesFilterValues,
    ];

    if (userType === AccountType.Performer) {
      filterSet.push(allPaidFilterValues);
    }

    try {
      counts = await getJobCounts(filterSet, cancellationToken);
    } catch (e) {
      console.error(e);
    }

    return counts;
  }

  private async loadClientAlertsCount() {
    const { getClientAlerts, cancellationToken } = this.props;

    let count: number | undefined;
    const clientAlertsValues = { skip: 0, take: 0 };

    try {
      count = (await getClientAlerts(clientAlertsValues, cancellationToken))
        .unreadTotal;
    } catch (e) {
      console.error(e);
    }

    return count;
  }

  private async loadSelfTapeCount() {
    const { getSelfTapeCount, cancellationToken } = this.props;

    let count: number | undefined;

    try {
      count = await getSelfTapeCount(cancellationToken);
    } catch (e) {
      console.error(e);
    }

    return count;
  }

  private async loadMySuggestionsCount() {
    try {
      return await getMySuggestionsCount();
    } catch (e) {
      console.error("error", e);
    }
  }
}

async function loadExternalData(props: IJobsPageProps) {
  return props.getFacets(
    [FacetCategory.ProductionType, FacetCategory.ContractType],
    props.locale
  );
}

const pageDataLoaders: ILoaders<IJobsPageProps> = {
  async loadTranslation(props, isLocaleChanged) {
    await props.getPageTranslation(
      { pageKey: PageKey.SelfTapeRequestNotifications, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );

    return props.getPageTranslation(
      { pageKey: PageKey.Jobs, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  async loadData(props) {
    await loadExternalData(props);

    return props.getJobsPage();
  },
  loadOnLocaleChange: loadExternalData,
};

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageProps
): IJobsPageStoreProps => {
  const page = state.translation.pages[PageKey.Jobs]!;
  const pageData = state.page.data!;
  const jobSettings =
    ownProps.userType === AccountType.Performer
      ? pageData.jobSettings!.performerSettings!
      : pageData.jobSettings!.agentSettings!;

  return {
    breadcrumbs: prepareBreadcrumbs(page, myAccountLink),
    tabs: prepareTabs(),
    commonTexts: prepareCommonTexts(
      page,
      ownProps.userType,
      ownProps.match.params.tab
    ),
    pageTranslationLocale: state.translation.pageLocale,
    facetLocale: state.facet.locale,
    productionTypes: state.facet[FacetStoreKey.ProductionType]!,
    contractTypes: state.facet[FacetStoreKey.ContractType]!,
    jobSettings,
    getJobCounts: (
      filters: IJobFilterParams[],
      cancellationToken?: CancelToken
    ) => jobService.getCounts(filters, cancellationToken),
    getClientAlerts: (...args) => suggestionsService.getClientAlerts(...args),
    getSelfTapeCount: (...args) =>
      selfTapeRequestNotificationService.getCount(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<IJobsPageActions> = {
  getPageTranslation,
  setLoading,
  showPopup,
  hidePopup,
  getJobsPage,
  getFacets,
};

export default compose(
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withQueryParameters(({ page }) => ({ page: page || 1 })),
  withPageData<IJobsPageProps>(PageKey.Jobs, pageDataLoaders),
  connect(mapStateToProps, mapDispatchToProps)
)(JobsPage);
